import { useCallback, useEffect, useMemo, useState } from 'react';

import axios from 'axios';
import { useQuery } from 'react-query';

import processingActivitiesApi from 'config/api/processingActivities/processingActivities';
import type { PaginatedProcessingActivitiesResponse, ProcessingActivity } from 'config/api/processingActivities/processingActivities.types';
import QUERY_KEYS from 'config/api/QUERY_KEYS';

import {
  buildProcessingActivitiesListParams,
  type ProcessingActivitiesFilterValues,
  type ProcessingActivitiesSort,
} from './processingActivitiesFilters';

type UseProcessingActivitiesListDataOptions = {
  filters: ProcessingActivitiesFilterValues;
  organizationId: number | undefined;
  pageSize: number;
  search: string;
  sort: ProcessingActivitiesSort;
};

const useProcessingActivitiesListData = ({ filters, organizationId, pageSize, search, sort }: UseProcessingActivitiesListDataOptions) => {
  const [page, setPage] = useState(1);
  const [accumulatedActivities, setAccumulatedActivities] = useState<ProcessingActivity[]>([]);

  const listParams = useMemo(
    () =>
      buildProcessingActivitiesListParams({
        filters,
        page,
        pageSize,
        search,
        sort,
      }),
    [filters, page, pageSize, search, sort],
  );

  // Reset pagination on filter/search/sort change without wiping the current rows —
  // previous rows stay visible until the new query resolves (live filter UX).
  useEffect(() => {
    setPage(1);
  }, [organizationId, filters, search, sort]);

  const {
    data: pageData,
    error: queryError,
    isError,
    isFetching,
    isLoading,
    isPreviousData,
    refetch,
  } = useQuery(
    [QUERY_KEYS.PROCESSING_ACTIVITIES_GET_ALL, organizationId, listParams],
    async (): Promise<PaginatedProcessingActivitiesResponse> => {
      const { data } = await processingActivitiesApi.getAll(organizationId as number, listParams);
      return data;
    },
    {
      enabled: organizationId != null,
      keepPreviousData: true,
    },
  );

  const isForbidden = Boolean(isError && axios.isAxiosError(queryError) && queryError.response?.status === 403);

  useEffect(() => {
    if (!pageData || isPreviousData) return;

    if (page === 1) {
      setAccumulatedActivities(pageData.results);
      return;
    }

    setAccumulatedActivities(previousActivities => {
      const existingIds = new Set(previousActivities.map(activity => activity.id));
      return [...previousActivities, ...pageData.results.filter(activity => !existingIds.has(activity.id))];
    });
  }, [isPreviousData, page, pageData]);

  const hasNextPage = !isPreviousData && pageData?.next != null && pageData.next !== '';

  const loadNextPage = useCallback(() => {
    setPage(currentPage => currentPage + 1);
  }, []);

  return {
    accumulatedActivities,
    hasNextPage,
    isError,
    isFetching,
    isForbidden,
    isLoading,
    loadNextPage,
    page,
    refetch,
  };
};

export default useProcessingActivitiesListData;
